import { useAppForm } from '@/components/form/formHooks';
import { FormValidationSchema } from '@/domain/termine/features/CreateTermin/shared/FormValidationSchema';
import { dateMapper } from '@/domain/termine/features/CreateTermin/Sheet/utils/formMappers';
import { useCreateTermine } from '@/domain/termine/hooks/useCreateTermine';
import { useDefaultDate } from '@/domain/termine/hooks/useDefaultDate';
import {
  FormValues,
  markierungTypes,
  TerminWiederholung,
} from '@/domain/termine/shared/types';
import { fileToBase64 } from '@/domain/termine/shared/utils/imageProcessing';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { useToast } from '@/hooks/use-toast';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import z from 'zod';

function formValuesToTerminInput(data: FormValues): FormValues {
  const mapped = dateMapper(data);

  return {
    titel: data.titel,
    beschreibung: data.beschreibung || '',
    datum: mapped.dateOnly,
    beginn: mapped.beginnDateTime || '',
    ende: mapped.endeDateTime || '',
    standortId: data.standortId,
    markierung: data.markierung,
    specials: data.specials || '',
    wiederholung: data.wiederholung || TerminWiederholung.OHNE,
    wiederholungBis: mapped.wiederholungBisTime,
    terminBild: data.terminBild,
    terminKalenderId: data.terminKalenderId,
  };
}

// eslint-disable-next-line max-lines-per-function
export function useCreateTermineForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();

  const { toast } = useToast();
  const { createTermin } = useCreateTermine();

  const defaultDate = useDefaultDate();

  return useAppForm({
    defaultValues: {
      titel: '',
      beschreibung: '' as string | undefined,
      datum: defaultDate.toISOString(),
      beginn: '',
      ende: '',
      standortId: standortId,
      markierung: markierungTypes[19],
      specials: '',
      wiederholung: TerminWiederholung.OHNE,
      wiederholungBis: undefined,
      terminBild: null,
      terminKalenderId: 'NO_KALENDER',
    } satisfies z.infer<ReturnType<typeof FormValidationSchema>> as z.infer<
      ReturnType<typeof FormValidationSchema>
    >,
    validators: {
      onBlur: FormValidationSchema(),
    },
    onSubmit: async ({ value }) => {
      const terminBild = value.terminBild;
      let terminBildInput: { name: string; base64Image: string } | undefined;
      if (terminBild?.file) {
        const base64Data = await fileToBase64(terminBild.file);
        terminBildInput = {
          name: terminBild.name || terminBild.file.name,
          base64Image: base64Data,
        };
      }

      const terminInput = {
        ...formValuesToTerminInput(value),
        terminBildInput,
        standortId,
      };

      const response = await createTermin(terminInput);

      if (!response) {
        throw Error('No response from createTermin mutation');
      }

      if (response.error) {
        toast({
          title: t('domain.termine.sheet.messages.terminErstellt.error.title'),
          description:
            response.error.message ||
            t('domain.termine.sheet.messages.terminErstellt.error.description'),
          variant: 'destructive',
        });
      } else {
        toast({
          title: t('domain.termine.sheet.messages.terminErstellt.title', {
            name: value.titel,
          }),
        });
        const terminDate = dayjs(value.datum);
        const terminYear = terminDate.format('YYYY');
        const terminMonth = terminDate.format('M');
        const terminDay = terminDate.format('D');
        navigate(
          `/termine/uebersicht/${terminYear}/${terminMonth}/${terminDay}`,
        );
      }
    },
  });
}
